import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mic, MicOff, Sparkles, X } from "lucide-react";
import { REGAL_WORLDS } from "../data/regalWorlds";
import { filterProducts, getProduct, isWishlisted, toggleWishlist, fetchGoldRates } from "../lib/store";
import { loadAliases, norm, similarity, expandQuery, resolveWorld, topSuggestions, logVoiceMiss } from "../lib/voiceAliases";
import { trackSearch } from "../lib/analytics";

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

const LANGS = [
  { code: "en-US", label: "EN" },
  { code: "hi-IN", label: "हिं" },
  { code: "pa-IN", label: "ਪੰ" },
];

const WAKE_TARGETS = ["hey regal", "हे रीगल", "ਹੇ ਰੀਗਲ"];
const WAKE_VARIANTS = [
  "hey regal", "hi regal", "ok regal", "hey regale", "hey regel", "hey rigal", "hey riegel", "a regal", "hey regals", "heyregal", "hey regan", "hey regard",
  "हे रीगल", "हे रिगल", "हे रेगल", "है रीगल", "हे रीगल जी", "हे रीगेल",
  "ਹੇ ਰੀਗਲ", "ਹੇ ਰਿਗਲ", "ਹੈ ਰੀਗਲ", "ਹੇ ਰੀਗਲ ਜੀ",
];
const FILLER_WORDS = new Set([
  "show", "me", "find", "search", "for", "please", "get", "want", "see", "some", "i", "us", "looking",
  "दिखाओ", "दिखाइए", "दिखा", "दो", "मुझे", "चाहिए", "चाहिये", "खोजो", "ढूंढो", "कृपया", "करो", "जी", "हमें", "कुछ",
  "ਦਿਖਾਓ", "ਵਿਖਾਓ", "ਮੈਨੂੰ", "ਲੱਭੋ", "ਚਾਹੀਦਾ", "ਚਾਹੀਦੇ", "ਜੀ", "ਕਰੋ", "ਕੁਝ",
]);
const WORLD_VERBS = /\b(open|go to|take me to|visit|enter)\b/i;
const WORLD_VERBS_NATIVE = /(खोलो|खोलिए|ले चलो|चलो|ਖੋਲੋ|ਲੈ ਚਲੋ|ਚਲੋ)/;
const WORLD_WORDS = /\b(world|collection|studio|exchange)\b/i;
const WORLD_WORDS_NATIVE = /(वर्ल्ड|कलेक्शन|ਵਰਲਡ|ਕਲੈਕਸ਼ਨ)/;
const QUESTION_EN = /^(what|whats|what's|why|how|when|where|which|who|is|are|do|does|can|should|tell me|explain)\b/i;
const QUESTION_NATIVE = /(क्या|कैसे|क्यों|कब|कौन|कितन|बताओ|बताइए|समझाओ|ਕੀ|ਕਿਵੇਂ|ਕਿਉਂ|ਕਦੋਂ|ਦੱਸੋ|ਕਿੰਨ)/;
const WISHLIST_RE = /(save this|save it|add to wishlist|wishlist this|wishlist it|इसे सेव|सेव करो|सेव कर|पसंद में|विशलिस्ट|ਸੇਵ ਕਰੋ|ਸੇਵ ਕਰ|ਵਿਸ਼ਲਿਸਟ|ਪਸੰਦ ਵਿੱਚ)/i;
const GOLD_RATE_RE = /(gold\s*(rate|price)|(rate|price)\s*of\s*gold|gold\s*(ka|da)\s*(rate|bhav|bhaav)|sone?\s*(ka|da|de)\s*(rate|bhav|bhaav|bha|daam|dam)|सोने\s*का\s*(भाव|रेट|दाम)|सोना\s*(भाव|रेट)|गोल्ड\s*(रेट|प्राइस)|ਸੋਨੇ\s*(ਦਾ|ਦੇ)\s*(ਭਾਅ|ਰੇਟ)|ਗੋਲਡ\s*(ਰੇਟ|ਪ੍ਰਾਈਸ))/i;
const RATE_ALERT_RE = /((alert|notify|remind|tell me|let me know|watch|track|message me|whatsapp me|ping me).*(gold|rate|price))|(gold.*(drop|fall|below|under|reach|hit|goes down))|((price|rate)\s*alert)|(सोन.*(अलर्ट|बताना|खबर|गिर|कम हो))|(अलर्ट.*सोन)|(ਸੋਨ.*(ਅਲਰਟ|ਦੱਸ|ਖ਼ਬਰ|ਡਿੱਗ|ਘਟ))/i;

const PHRASES = {
  "en-US": {
    opening: (t) => `Opening ${t} for you.`,
    here: (q) => `Here you are — our finest ${q}.`,
    didYouMean: (s) => `I did not find that exactly — did you mean ${s}?`,
    question: "A fine question — allow me.",
    chat: "Let me answer that in the chat.",
    saved: "Saved to your wishlist.",
    already: "This piece is already in your wishlist.",
    savePrompt: "Open a piece first, then say — save this.",
    hereShort: (q) => `Here are our ${q}.`,
    goldRate: (r) => `Today's gold rate is ${r.k22} dollars per gram for 22 karat, ${r.k24} for 24 karat, and ${r.k18} for 18 karat.`,
    goldFail: "I could not reach today's rate — please ask again in a moment.",
    rateAlert: "Of course — let me set up your gold rate alert in the chat.",
  },
  "hi-IN": {
    opening: (t) => `${t} खोल रहा हूं।`,
    here: (q) => `ये रहे आपके ${q}।`,
    didYouMean: (s) => `ठीक वैसा नहीं मिला — क्या आपका मतलब ${s} था?`,
    question: "बहुत अच्छा प्रश्न — बताता हूं।",
    chat: "इसका उत्तर मैं चैट में देता हूं।",
    saved: "आपकी विशलिस्ट में सेव कर दिया।",
    already: "यह पीस पहले से आपकी विशलिस्ट में है।",
    savePrompt: "पहले कोई पीस खोलिए, फिर कहिए — इसे सेव करो।",
    hereShort: (q) => `ये रहे ${q}।`,
    goldRate: (r) => `आज का सोने का भाव — 22 कैरेट ${r.k22} डॉलर प्रति ग्राम, 24 कैरेट ${r.k24} डॉलर, और 18 कैरेट ${r.k18} डॉलर।`,
    goldFail: "अभी भाव नहीं मिल पाया — कृपया थोड़ी देर में फिर पूछिए।",
    rateAlert: "ज़रूर — मैं चैट में आपका गोल्ड रेट अलर्ट सेट करता हूं।",
  },
  "pa-IN": {
    opening: (t) => `${t} ਖੋਲ੍ਹ ਰਿਹਾ ਹਾਂ।`,
    here: (q) => `ਇਹ ਰਹੇ ਤੁਹਾਡੇ ${q}।`,
    didYouMean: (s) => `ਬਿਲਕੁਲ ਉਹ ਨਹੀਂ ਮਿਲਿਆ — ਕੀ ਤੁਹਾਡਾ ਮਤਲਬ ${s} ਸੀ?`,
    question: "ਬਹੁਤ ਵਧੀਆ ਸਵਾਲ — ਦੱਸਦਾ ਹਾਂ।",
    chat: "ਇਸ ਦਾ ਜਵਾਬ ਮੈਂ ਚੈਟ ਵਿੱਚ ਦਿੰਦਾ ਹਾਂ।",
    saved: "ਤੁਹਾਡੀ ਵਿਸ਼ਲਿਸਟ ਵਿੱਚ ਸੇਵ ਕਰ ਦਿੱਤਾ।",
    already: "ਇਹ ਪੀਸ ਪਹਿਲਾਂ ਹੀ ਤੁਹਾਡੀ ਵਿਸ਼ਲਿਸਟ ਵਿੱਚ ਹੈ।",
    savePrompt: "ਪਹਿਲਾਂ ਕੋਈ ਪੀਸ ਖੋਲ੍ਹੋ, ਫਿਰ ਕਹੋ — ਸੇਵ ਕਰੋ।",
    hereShort: (q) => `ਇਹ ਰਹੇ ${q}।`,
    goldRate: (r) => `ਅੱਜ ਦਾ ਸੋਨੇ ਦਾ ਭਾਅ — 22 ਕੈਰੇਟ ${r.k22} ਡਾਲਰ ਪ੍ਰਤੀ ਗ੍ਰਾਮ, 24 ਕੈਰੇਟ ${r.k24} ਡਾਲਰ, ਅਤੇ 18 ਕੈਰੇਟ ${r.k18} ਡਾਲਰ।`,
    goldFail: "ਹੁਣੇ ਭਾਅ ਨਹੀਂ ਮਿਲ ਸਕਿਆ — ਕਿਰਪਾ ਕਰਕੇ ਫਿਰ ਪੁੱਛੋ।",
    rateAlert: "ਜ਼ਰੂਰ — ਮੈਂ ਚੈਟ ਵਿੱਚ ਤੁਹਾਡਾ ਗੋਲਡ ਰੇਟ ਅਲਰਟ ਸੈੱਟ ਕਰਦਾ ਹਾਂ।",
  },
};

const HINTS = {
  "en-US": {
    title: "Try saying",
    tip: "Tap the mic below, then speak — or tap a phrase",
    items: [
      { say: "“Hey Regal, show me gold bangles”", cmd: "show me gold bangles" },
      { say: "“Hey Regal, open Bridal World”", cmd: "open bridal world" },
      { say: "“Hey Regal, what's today's gold rate?”", cmd: "what is today's gold rate" },
    ],
  },
  "hi-IN": {
    title: "बोलकर देखिए",
    tip: "नीचे माइक दबाइए, फिर बोलिए — या वाक्य पर टैप कीजिए",
    items: [
      { say: "“हे रीगल, मुझे कंगन दिखाओ”", cmd: "मुझे कंगन दिखाओ" },
      { say: "“हे रीगल, दुल्हन कलेक्शन खोलो”", cmd: "दुल्हन कलेक्शन खोलो" },
      { say: "“हे रीगल, आज सोने का भाव क्या है?”", cmd: "आज सोने का भाव क्या है" },
    ],
  },
  "pa-IN": {
    title: "ਬੋਲ ਕੇ ਵੇਖੋ",
    tip: "ਹੇਠਾਂ ਮਾਈਕ ਦਬਾਓ, ਫਿਰ ਬੋਲੋ — ਜਾਂ ਵਾਕ ਤੇ ਟੈਪ ਕਰੋ",
    items: [
      { say: "“ਹੇ ਰੀਗਲ, ਮੈਨੂੰ ਝੁਮਕੇ ਦਿਖਾਓ”", cmd: "ਮੈਨੂੰ ਝੁਮਕੇ ਦਿਖਾਓ" },
      { say: "“ਹੇ ਰੀਗਲ, ਗੋਲਡ ਵਰਲਡ ਖੋਲ੍ਹੋ”", cmd: "ਗੋਲਡ ਵਰਲਡ ਖੋਲ੍ਹੋ" },
      { say: "“ਹੇ ਰੀਗਲ, ਅੱਜ ਸੋਨੇ ਦਾ ਭਾਅ ਕੀ ਹੈ?”", cmd: "ਅੱਜ ਸੋਨੇ ਦਾ ਭਾਅ ਕੀ ਹੈ" },
    ],
  },
};

const GOLD_UI = {
  "en-US": { title: "Today's Gold Rate", gram: "per gram", tola: "per tola (22K)", updated: "Live spot price" },
  "hi-IN": { title: "आज का सोने का भाव", gram: "प्रति ग्राम", tola: "प्रति तोला (22K)", updated: "लाइव भाव" },
  "pa-IN": { title: "ਅੱਜ ਦਾ ਸੋਨੇ ਦਾ ਭਾਅ", gram: "ਪ੍ਰਤੀ ਗ੍ਰਾਮ", tola: "ਪ੍ਰਤੀ ਤੋਲਾ (22K)", updated: "ਲਾਈਵ ਭਾਅ" },
};

function speak(text, lang = "en-US") {
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang;
    const voices = window.speechSynthesis.getVoices();
    const short = lang.split("-")[0];
    u.voice =
      voices.find((v) => v.lang?.toLowerCase().startsWith(short) && /male|daniel|george/i.test(v.name)) ||
      voices.find((v) => v.lang?.toLowerCase().startsWith(short)) ||
      voices.find((v) => v.lang?.startsWith("en")) || null;
    u.rate = 0.96;
    u.pitch = 0.85;
    window.speechSynthesis.speak(u);
  } catch (e) {
    console.debug("tts failed:", e?.message);
  }
}

function detectWake(text) {
  const t = norm(text);
  for (const w of WAKE_VARIANTS) {
    const idx = t.indexOf(w);
    if (idx !== -1) return { rest: t.slice(idx + w.length).trim() };
  }
  const tokens = t.split(" ");
  for (let i = 0; i < tokens.length - 1; i++) {
    const bigram = `${tokens[i]} ${tokens[i + 1]}`;
    for (const target of WAKE_TARGETS) {
      if (similarity(bigram, target) >= 0.7) return { rest: tokens.slice(i + 2).join(" ").trim() };
    }
  }
  return null;
}

const stripFillers = (cmd) => cmd.split(" ").filter((t) => t && !FILLER_WORDS.has(t)).join(" ");

export default function HeyRegal() {
  const [enabled, setEnabled] = useState(() => sessionStorage.getItem("rj_heyregal") === "1");
  const [mode, setMode] = useState("off"); // off | passive | awake
  const [lang, setLang] = useState(() => localStorage.getItem("rj_voice_lang") || "en-US");
  const [heard, setHeard] = useState("");
  const [suggestions, setSuggestions] = useState(null);
  const [goldRate, setGoldRate] = useState(null);
  const [hints, setHints] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const recRef = useRef(null);
  const enabledRef = useRef(enabled);
  const langRef = useRef(lang);
  const speakingRef = useRef(false);
  const awakeTimer = useRef(null);
  const hintTimer = useRef(null);
  const goldTimer = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  const stopRec = () => {
    try { recRef.current?.abort(); } catch (e) { console.debug("rec abort:", e?.message); }
    recRef.current = null;
  };

  const showHints = useCallback(() => {
    setHints(true);
    clearTimeout(hintTimer.current);
    hintTimer.current = setTimeout(() => setHints(false), 15000);
  }, []);

  // Voice hint tour — auto-show once on a visitor's first visit
  useEffect(() => {
    if (localStorage.getItem("rj_voice_hints")) return undefined;
    const t = setTimeout(() => {
      localStorage.setItem("rj_voice_hints", "1");
      showHints();
    }, 2500);
    return () => { clearTimeout(t); clearTimeout(hintTimer.current); };
  }, [showHints]);

  const execute = useCallback(async (raw) => {
    const aliases = await loadAliases();
    const L = PHRASES[langRef.current] || PHRASES["en-US"];
    const cmd = norm(raw);
    if (!cmd) return;
    setHeard(raw);
    setSuggestions(null);
    setGoldRate(null);
    setHints(false);

    // 1) "Save this" → wishlist the piece on screen
    if (WISHLIST_RE.test(cmd)) {
      const path = pathRef.current;
      if (path.startsWith("/product/")) {
        const handle = decodeURIComponent(path.split("/product/")[1].split("/")[0]);
        const p = getProduct(handle);
        if (p) {
          if (isWishlisted(handle)) speak(L.already, langRef.current);
          else {
            toggleWishlist(handle);
            speak(L.saved, langRef.current);
          }
          return;
        }
      }
      speak(L.savePrompt, langRef.current);
      return;
    }

    // 2) Gold rate alert — "tell me when gold drops below…" → open chat form
    if (RATE_ALERT_RE.test(cmd)) {
      speak(L.rateAlert, langRef.current);
      window.dispatchEvent(new CustomEvent("rj-open-chat"));
      setTimeout(() => window.dispatchEvent(new CustomEvent("rj-chat-ask", { detail: { q: raw } })), 500);
      return;
    }

    // 3) Today's gold rate — spoken live, with an on-screen card
    if (GOLD_RATE_RE.test(cmd)) {
      try {
        const r = await fetchGoldRates();
        setGoldRate(r);
        clearTimeout(goldTimer.current);
        goldTimer.current = setTimeout(() => setGoldRate(null), 45000);
        speak(L.goldRate({
          k24: Math.round(r.perGram.k24),
          k22: Math.round(r.perGram.k22),
          k18: Math.round(r.perGram.k18),
        }), langRef.current);
      } catch (e) {
        console.debug("gold rate:", e?.message);
        speak(L.goldFail, langRef.current);
      }
      return;
    }

    // 3) Open a world/collection
    if (WORLD_VERBS.test(cmd) || WORLD_VERBS_NATIVE.test(cmd) || WORLD_WORDS.test(cmd) || WORLD_WORDS_NATIVE.test(cmd)) {
      const hit = resolveWorld(stripFillers(cmd), aliases, REGAL_WORLDS);
      if (hit) {
        const world = REGAL_WORLDS.find((w) => w.slug === hit.slug);
        speak(L.opening(world ? world.title : "that collection"), langRef.current);
        navigate(`/regal-world/${hit.slug}`);
        return;
      }
    }

    // 4) Question → Mr. Regal chat
    if (QUESTION_EN.test(cmd) || QUESTION_NATIVE.test(cmd)) {
      speak(L.question, langRef.current);
      window.dispatchEvent(new CustomEvent("rj-open-chat"));
      setTimeout(() => window.dispatchEvent(new CustomEvent("rj-chat-ask", { detail: { q: raw } })), 500);
      return;
    }

    // 5) Product search: alias expansion + fuzzy fallback cascade
    const cleaned = stripFillers(cmd);
    const q = expandQuery(cleaned, aliases);
    let finalQ = q;
    let results = q ? filterProducts({ search: q }) : [];
    if (results.length === 0 && q) {
      const canon = new Set(aliases.filter((a) => a.type !== "world").map((a) => a.canonical));
      const known = q.split(" ").filter((t) => canon.has(t));
      for (const cand of [known.join(" "), known[0]]) {
        if (!cand || cand === q) continue;
        const r = filterProducts({ search: cand });
        if (r.length > 0) { finalQ = cand; results = r; break; }
      }
    }
    if (results.length > 0) {
      trackSearch(finalQ);
      speak(L.here(finalQ), langRef.current);
      navigate(`/shop?search=${encodeURIComponent(finalQ)}`);
      return;
    }
    logVoiceMiss(cmd, q, langRef.current, pathRef.current);
    const sugg = topSuggestions(cleaned || cmd, aliases, 3)
      .map((s) => ({ ...s, count: filterProducts({ search: s.canonical }).length }))
      .filter((s) => s.count > 0);
    if (sugg.length > 0 && sugg[0].score >= 0.4) {
      setSuggestions(sugg.slice(0, 3));
      speak(L.didYouMean(sugg[0].canonical), langRef.current);
      return;
    }
    speak(L.chat, langRef.current);
    window.dispatchEvent(new CustomEvent("rj-open-chat"));
    setTimeout(() => window.dispatchEvent(new CustomEvent("rj-chat-ask", { detail: { q: raw } })), 500);
  }, [navigate]);

  const startRec = useCallback(() => {
    if (!SR || !enabledRef.current || speakingRef.current || recRef.current) return;
    const rec = new SR();
    recRef.current = rec;
    rec.lang = langRef.current;
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      const res = e.results[e.results.length - 1];
      const text = res[0]?.transcript || "";
      const wake = detectWake(text);
      if (wake && wake.rest.length > 2 && res.isFinal) {
        setMode("passive");
        clearTimeout(awakeTimer.current);
        execute(wake.rest);
        return;
      }
      if (wake) {
        setMode("awake");
        clearTimeout(awakeTimer.current);
        awakeTimer.current = setTimeout(() => setMode("passive"), 8000);
        return;
      }
      if (res.isFinal && norm(text).length > 2) {
        setMode((m) => {
          if (m === "awake") {
            clearTimeout(awakeTimer.current);
            execute(text);
            return "passive";
          }
          return m;
        });
      }
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        enabledRef.current = false;
        setEnabled(false);
        sessionStorage.removeItem("rj_heyregal");
        setMode("off");
        setHeard("mic-blocked");
      }
      if (e.error === "language-not-supported") {
        langRef.current = "en-US";
        setLang("en-US");
        localStorage.setItem("rj_voice_lang", "en-US");
      }
    };
    rec.onend = () => {
      recRef.current = null;
      if (enabledRef.current && !speakingRef.current) setTimeout(() => startRec(), 400);
    };
    try {
      rec.start();
      setMode((m) => (m === "awake" ? m : "passive"));
    } catch (e) {
      console.debug("rec start:", e?.message);
      recRef.current = null;
    }
  }, [execute]);

  // Pause listening while Mr. Regal speaks so he doesn't hear himself
  useEffect(() => {
    const h = (e) => setChatOpen(Boolean(e.detail?.open));
    window.addEventListener("rj-chat-state", h);
    return () => window.removeEventListener("rj-chat-state", h);
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      const speaking = window.speechSynthesis?.speaking || false;
      if (speaking !== speakingRef.current) {
        speakingRef.current = speaking;
        if (speaking) stopRec();
        else if (enabledRef.current) startRec();
      }
    }, 350);
    return () => clearInterval(iv);
  }, [startRec]);

  useEffect(() => {
    enabledRef.current = enabled;
    if (enabled) {
      startRec();
      setMode("passive");
    } else {
      stopRec();
      setMode("off");
      clearTimeout(awakeTimer.current);
    }
    return stopRec;
  }, [enabled, startRec]);

  if (!SR) return null;

  const toggle = () => {
    const next = !enabled;
    if (next) sessionStorage.setItem("rj_heyregal", "1");
    else sessionStorage.removeItem("rj_heyregal");
    setHeard("");
    setSuggestions(null);
    setEnabled(next);
    if (next && !localStorage.getItem("rj_voice_hints_mic")) {
      localStorage.setItem("rj_voice_hints_mic", "1");
      showHints();
    }
  };

  const pickLang = (code) => {
    setLang(code);
    langRef.current = code;
    localStorage.setItem("rj_voice_lang", code);
    if (enabledRef.current) {
      stopRec();
      setTimeout(() => startRec(), 200);
    }
  };

  const pickSuggestion = (s) => {
    setSuggestions(null);
    trackSearch(s.canonical);
    speak((PHRASES[langRef.current] || PHRASES["en-US"]).hereShort(s.canonical), langRef.current);
    navigate(`/shop?search=${encodeURIComponent(s.canonical)}`);
  };

  const H = HINTS[lang] || HINTS["en-US"];
  const G = GOLD_UI[lang] || GOLD_UI["en-US"];

  return (
    <div className={`fixed bottom-[76px] left-3 sm:left-4 z-[45] flex-col items-start gap-2 ${chatOpen ? "hidden" : "flex"}`} data-testid="heyregal-root">
      {goldRate && (
        <div className="bg-white border border-[#c9a24b] shadow-lg px-4 py-3 max-w-[92vw] w-[270px]" data-testid="heyregal-goldrate-card">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#8a6a1f]">{G.title}</p>
            <button onClick={() => setGoldRate(null)} data-testid="heyregal-goldrate-close" aria-label="Close gold rate" className="text-[#8f8571] hover:text-[#171310]">
              <X size={12} />
            </button>
          </div>
          <div className="mt-2 space-y-1">
            {[["24K", goldRate.perGram.k24], ["22K", goldRate.perGram.k22], ["18K", goldRate.perGram.k18]].map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between text-[13px]">
                <span className="text-[#5c5240]">{k} <span className="text-[10px] text-[#9c9482]">{G.gram}</span></span>
                <span className="font-heading text-[#171310]">${v.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex items-baseline justify-between text-[12px] border-t border-[#eee6d6] pt-1 mt-1">
              <span className="text-[#5c5240]">{G.tola}</span>
              <span className="font-heading text-[#171310]">${goldRate.perTola.k22.toFixed(2)}</span>
            </div>
          </div>
          <p className="mt-1.5 text-[9px] uppercase tracking-[0.12em] text-[#9c9482]">{G.updated}</p>
        </div>
      )}
      {hints && !suggestions && !goldRate && (
        <div className="bg-white border border-[#c9a24b]/70 shadow-lg px-4 py-3 max-w-[92vw] w-[290px]" data-testid="heyregal-hints">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#8a6a1f]">{H.title}</p>
            <div className="flex items-center gap-1">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => pickLang(l.code)} data-testid={`heyregal-hint-lang-${l.code}`}
                  className={`text-[11px] leading-tight px-2 py-1 border transition-colors ${
                    lang === l.code ? "bg-[#171310] text-[#e9d9ac] border-[#171310]" : "text-[#5c5240] border-[#d8cdb5] hover:border-[#a8842c]"
                  }`}>
                  {l.label}
                </button>
              ))}
              <button onClick={() => setHints(false)} data-testid="heyregal-hints-close" aria-label="Close voice tips" className="ml-1 text-[#8f8571] hover:text-[#171310]">
                <X size={12} />
              </button>
            </div>
          </div>
          <div className="mt-2 space-y-1.5">
            {H.items.map((it, i) => (
              <button key={it.cmd} onClick={() => { setHints(false); execute(it.cmd); }} data-testid={`heyregal-hint-${i}`}
                className="block w-full text-left text-[12px] text-[#3a352c] hover:text-[#a8842c] transition-colors leading-snug">
                {it.say}
              </button>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-[#8f8571]">{H.tip}</p>
        </div>
      )}
      {suggestions && (
        <div className="bg-white border border-[#d8cdb5] shadow-lg px-4 py-3 max-w-[92vw]" data-testid="heyregal-suggestions">
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#8a6a1f]">Did you mean</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {suggestions.map((s, i) => (
              <button key={s.canonical} onClick={() => pickSuggestion(s)} data-testid={`heyregal-suggestion-${i}`}
                className="text-xs border border-[#d8cdb5] px-3 py-1.5 hover:border-[#a8842c] hover:text-[#a8842c] transition-colors capitalize">
                {s.canonical} ({s.count})
              </button>
            ))}
          </div>
        </div>
      )}
      {enabled && (
        <div className="flex gap-1" data-testid="heyregal-langs">
          {LANGS.map((l) => (
            <button key={l.code} onClick={() => pickLang(l.code)} data-testid={`heyregal-lang-${l.code}`}
              className={`text-[11px] leading-tight px-2 py-1 border backdrop-blur transition-colors ${
                lang === l.code ? "bg-[#171310] text-[#e9d9ac] border-[#171310]" : "bg-white/95 text-[#5c5240] border-[#d8cdb5] hover:border-[#a8842c]"
              }`}>
              {l.label}
            </button>
          ))}
        </div>
      )}
      <button
        onClick={toggle}
        data-testid="heyregal-toggle"
        aria-label={enabled ? "Turn off Hey Regal voice commands" : "Turn on Hey Regal voice commands"}
        className={`flex items-center justify-center gap-2 h-11 w-11 rounded-full p-0 sm:h-9 sm:w-auto sm:rounded-none sm:px-4 border text-[11px] tracking-[0.14em] uppercase backdrop-blur shadow-sm transition-colors ${
          mode === "awake"
            ? "bg-[#a8842c] text-white border-[#a8842c]"
            : enabled
              ? "bg-white/95 text-[#8a6a1f] border-[#c9a24b]"
              : "bg-white/95 text-[#5c5240] border-[#d8cdb5] hover:border-[#a8842c] hover:text-[#a8842c]"
        }`}
        data-mode={mode}
      >
        {enabled ? <Mic size={14} className={mode === "awake" ? "animate-pulse" : ""} /> : <MicOff size={14} />}
        <span className="hidden sm:inline">{mode === "awake" ? "Listening…" : enabled ? "Say “Hey Regal”" : "Hey Regal"}</span>
        {mode === "awake" && <Sparkles size={12} className="hidden sm:inline animate-pulse" />}
      </button>
      {heard === "mic-blocked" && enabled === false && (
        <p className="text-[10px] text-[#8f2f2f] bg-white/90 px-2 py-1" data-testid="heyregal-mic-blocked">Microphone blocked — allow mic access to use Hey Regal</p>
      )}
    </div>
  );
}
